import React, { useState, useRef } from 'react'
import {
  Box,
  Button,
  Typography,
  Alert,
  LinearProgress,
  Chip,
  Paper
} from '@mui/material'
import { CloudUpload as UploadIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { filesAPI } from '../services/api'

const FileUpload = ({ 
  onFileUploaded, 
  onFileRemoved, 
  accept = '.pdf',
  maxSize = 50 * 1024 * 1024, // 50MB
  currentValue = null,
  required = false,
  label = 'Upload File',
  helperText = null,
  agentType = null,
  ticketId = null,
  acceptedFileTypes = []
}) => {
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState('')
  const [uploadedFile, setUploadedFile] = useState(currentValue ? {
    name: currentValue.split('/').pop(),
    path: currentValue
  } : null)
  
  const fileInputRef = useRef(null)

  const handleFileSelect = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    // Validate file type based on acceptedFileTypes or fallback to PDF
    const fileName = file.name.toLowerCase()
    const fileExtension = fileName.split('.').pop()
    
    const allowedTypes = acceptedFileTypes.length > 0 ? acceptedFileTypes : ['pdf']
    const allowedTypesLower = allowedTypes.map(type => type.toLowerCase())
    
    if (!allowedTypesLower.includes(fileExtension)) {
      const typesText = allowedTypes.map(type => type.toUpperCase()).join(', ')
      setError(`Only ${typesText} files are supported`)
      return
    }

    // Validate file size
    if (file.size > maxSize) {
      setError(`File size must be less than ${Math.round(maxSize / 1024 / 1024)}MB`)
      return
    }

    setError('')
    setUploading(true)
    setUploadProgress(0)

    try {
      // Create form data
      const formData = new FormData()
      formData.append('file', file)

      // Upload using the API service with agent type and ticket ID
      const result = await filesAPI.uploadFile(file, { 
        agentType: agentType,
        ticketId: ticketId 
      })
      
      if (result.success) {
        const fileInfo = {
          name: result.original_filename,
          path: result.file_path,
          size: result.size,
          storageType: result.storage_type
        }
        
        setUploadedFile(fileInfo)
        onFileUploaded(result.file_path, fileInfo)
        setUploadProgress(100)
      } else {
        throw new Error('Upload failed')
      }
    } catch (err) {
      setError(err.message || 'Upload failed')
      setUploadedFile(null)
    } finally {
      setUploading(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleRemoveFile = () => {
    setUploadedFile(null)
    setError('')
    setUploadProgress(0)
    onFileRemoved()
  }

  return (
    <Box>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Upload area */}
      {!uploadedFile && (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            textAlign: 'center',
            borderStyle: 'dashed',
            borderColor: error ? 'error.main' : 'divider',
            bgcolor: 'background.default',
            cursor: uploading ? 'not-allowed' : 'pointer',
            '&:hover': {
              bgcolor: uploading ? 'background.default' : 'action.hover'
            }
          }}
          onClick={uploading ? undefined : handleFileSelect}
        >
          <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {helperText || 'Click to browse or drag and drop your PDF file here'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Maximum file size: {Math.round(maxSize / 1024 / 1024)}MB
          </Typography>
          
          {!uploading && (
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              sx={{ mt: 2 }}
              onClick={(e) => {
                e.stopPropagation()
                handleFileSelect()
              }}
            >
              Choose File
            </Button>
          )}
        </Paper>
      )}

      {/* Upload progress */}
      {uploading && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            Uploading...
          </Typography>
          <LinearProgress 
            variant="indeterminate"
            sx={{ mb: 1 }}
          />
        </Box>
      )}

      {/* Uploaded file display */}
      {uploadedFile && !uploading && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'success.lighter' // or use a light green color
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label="PDF"
              size="small"
              color="success"
              variant="outlined"
            />
            <Typography variant="body2" noWrap>
              {uploadedFile.name}
            </Typography>
            {uploadedFile.size && (
              <Typography variant="caption" color="text.secondary">
                ({Math.round(uploadedFile.size / 1024)} KB)
              </Typography>
            )}
          </Box>
          <Button
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleRemoveFile}
          >
            Remove
          </Button>
        </Paper>
      )}

      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {/* Required field indicator */}
      {required && !uploadedFile && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          This field is required
        </Typography>
      )}
    </Box>
  )
}

export default FileUpload